import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ArrowRight,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  BarChart3,
  Users,
  Activity,
  Sun,
  Moon,
  Lock,
  Eye,
  EyeOff,
  FileText,
  MapPin,
  Award,
  Settings,
  CheckCircle2,
  ArrowLeft
} from "lucide-react";
import ThemeToggle from "@/components/common/ThemeToggle";

const __THEME_MARKER__ = true;
export default function Login() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [isDark, setIsDark] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // ---- Forgot Password state ----
  const [mode, setMode] = useState("login"); // "login" | "reset"
  const [resetEmail, setResetEmail] = useState("");
  const [resetOldPw, setResetOldPw] = useState("");
  const [resetNewPw, setResetNewPw] = useState("");
  const [resetConfirmPw, setResetConfirmPw] = useState("");
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  if (user) return <Navigate to="/dms" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav("/dms");
  };

  const openReset = () => {
    setResetEmail(email);
    setResetOldPw(""); setResetNewPw(""); setResetConfirmPw("");
    setResetError(""); setResetSuccess("");
    setMode("reset");
  };

  const backToLogin = () => {
    setResetError(""); setResetSuccess("");
    setMode("login");
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetError(""); setResetSuccess("");
    if (resetNewPw !== resetConfirmPw) {
      setResetError("New password and confirm password do not match");
      return;
    }
    setResetBusy(true);
    try {
      await api.post("/auth/reset-password-simple", {
        email: resetEmail.trim().toLowerCase(),
        old_password: resetOldPw,
        new_password: resetNewPw,
      });
      setResetSuccess("Password reset successful! You can now sign in with your new password.");
      setResetOldPw(""); setResetNewPw(""); setResetConfirmPw("");
      setTimeout(() => {
        setMode("login");
        setEmail(resetEmail);
        setResetSuccess("");
      }, 2000);
    } catch (err) {
      setResetError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className={`min-h-screen w-full theme-transition flex ${isDark ? "bg-[#050505]" : "bg-[#f7f5ef]"}`} data-testid="login-page">

      <button
        type="button"
        onClick={() => setIsDark(!isDark)}
        className={`fixed top-5 right-5 z-50 w-9 h-9 rounded-full border flex items-center justify-center transition ${
          isDark
            ? "border-[#d4a52b]/50 bg-black/60 hover:bg-black/80"
            : "border-[#c9971f]/40 bg-white hover:bg-[#f0ead9] shadow-sm"
        }`}
      >
        {isDark ? <Sun size={15} className="text-[#e8c568]" /> : <Moon size={15} className="text-[#8a6a10]" />}
      </button>

      <div className="relative hidden lg:flex lg:w-[52%] flex-col overflow-hidden">
        <img
          src="/login-assets/hero-night.jpg"
          alt="GCOIL"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? "linear-gradient(160deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.95) 100%)"
              : "linear-gradient(160deg, rgba(20,15,3,0.82) 0%, rgba(20,15,3,0.55) 45%, rgba(20,15,3,0.85) 100%)",
          }}
        />

        <div className="relative z-10 flex h-full flex-col justify-between px-10 py-10 xl:px-14 xl:py-12">

          <img src="/login-assets/logo.png" alt="GCOIL" className="h-11 w-auto object-contain" />

          <div className="max-w-lg">
            <div className="inline-block rounded-full border border-[#e5b52e]/40 bg-[#e5b52e]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#eab42f] mb-5">
              Distributor Management System
            </div>
            <h1 className="text-4xl xl:text-[3.2rem] font-black uppercase leading-[1.05] tracking-tight text-white">
              Smarter Distribution.
              <span className="block text-[#eab42f]">Stronger Growth.</span>
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-7 text-white/70">
              Real-time insights, streamlined operations, and complete control across your entire distribution network — anytime, anywhere.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-4 max-w-xl border-t border-white/10 pt-7">
            {[
              [FileText, "Sales & Purchase"],
              [BarChart3, "Real-Time Reports"],
              [MapPin, "Field Activity"],
              [Users, "Employee Monitoring"],
            ].map(([Icon, title]) => (
              <div key={title} className="flex flex-col items-start gap-2.5">
                <div className="w-9 h-9 rounded-lg border border-[#e5b52e]/30 bg-[#e5b52e]/5 flex items-center justify-center">
                  <Icon size={16} className="text-[#e5b52e]" />
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-white/80 leading-snug">{title}</div>
              </div>
            ))}
          </div>

        </div>
      </div>

      <div className={`flex-1 flex items-center justify-center px-6 py-12 sm:px-10 ${isDark ? "bg-[#0a0a0a]" : "bg-white"}`}>
        <div className="w-full max-w-sm">

          <div className="text-center">
            <div className={`mx-auto flex items-center justify-center rounded-2xl border p-3 w-fit ${
              isDark ? "border-[#d4a52b] bg-black" : "border-[#d4a52b] bg-[#050505]"
            }`}>
              <img src="/login-assets/logo.png" alt="GCOIL" className="h-10 w-auto object-contain" />
            </div>
            {mode === "login" ? (
              <>
                <h2 className={`mt-6 text-[26px] font-bold ${isDark ? "text-white" : "text-[#1a1a1a]"}`}>Welcome back!</h2>
                <p className={`mt-1.5 text-sm ${isDark ? "text-white/45" : "text-black/45"}`}>Sign in to continue to your dashboard.</p>
              </>
            ) : (
              <>
                <h2 className={`mt-6 text-[26px] font-bold ${isDark ? "text-white" : "text-[#1a1a1a]"}`}>Reset Password</h2>
                <p className={`mt-1.5 text-sm ${isDark ? "text-white/45" : "text-black/45"}`}>Enter your email and old password to set a new one.</p>
              </>
            )}
          </div>

          {mode === "login" ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="email" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>Email Address</Label>
                <div className="relative mt-2">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className={`h-11 pl-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pw" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>Password</Label>
                <div className="relative mt-2">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="pw"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className={`h-11 pl-10 pr-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3.5 top-1/2 -translate-y-1/2 hover:text-[#e5b52e] ${isDark ? "text-white/40" : "text-black/35"}`}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}

              <div className="flex items-center justify-between text-xs pt-1">
                <label className={`flex items-center gap-2 ${isDark ? "text-white/60" : "text-black/55"}`}>
                  <input type="checkbox" className="h-3.5 w-3.5 accent-[#dca92b]" />
                  Remember me
                </label>
                <button type="button" onClick={openReset} className="text-[#c9971f] hover:underline" data-testid="login-forgot-password-link">Reset Password</button>
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="h-11 w-full bg-gradient-to-r from-[#d39c1e] to-[#f0c34d] text-black font-bold text-sm hover:from-[#e5ad29] hover:to-[#ffd45c] transition"
                data-testid="login-submit"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    SIGN IN
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitReset} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="reset-email" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>Email Address</Label>
                <div className="relative mt-2">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    className={`h-11 pl-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="old-pw" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>Old Password</Label>
                <div className="relative mt-2">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="old-pw"
                    type={showOldPw ? "text" : "password"}
                    value={resetOldPw}
                    onChange={e => setResetOldPw(e.target.value)}
                    placeholder="Enter your current password"
                    className={`h-11 pl-10 pr-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPw(!showOldPw)}
                    className={`absolute right-3.5 top-1/2 -translate-y-1/2 hover:text-[#e5b52e] ${isDark ? "text-white/40" : "text-black/35"}`}
                  >
                    {showOldPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new-pw" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>New Password</Label>
                <div className="relative mt-2">
                  <LockKeyhole size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="new-pw"
                    type={showNewPw ? "text" : "password"}
                    value={resetNewPw}
                    onChange={e => setResetNewPw(e.target.value)}
                    placeholder="Min 8 chars, 1 uppercase, 1 digit"
                    className={`h-11 pl-10 pr-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className={`absolute right-3.5 top-1/2 -translate-y-1/2 hover:text-[#e5b52e] ${isDark ? "text-white/40" : "text-black/35"}`}
                  >
                    {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-pw" className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-white/70" : "text-black/60"}`}>Confirm New Password</Label>
                <div className="relative mt-2">
                  <LockKeyhole size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9971f]" />
                  <Input
                    id="confirm-pw"
                    type={showNewPw ? "text" : "password"}
                    value={resetConfirmPw}
                    onChange={e => setResetConfirmPw(e.target.value)}
                    placeholder="Re-enter new password"
                    className={`h-11 pl-10 focus:border-[#e5b52e] ${
                      isDark
                        ? "border-[#3a2c0e] bg-[#161616] text-white placeholder:text-white/30"
                        : "border-[#e3ddc9] bg-[#faf8f2] text-[#1a1a1a] placeholder:text-black/30"
                    }`}
                    required
                  />
                </div>
              </div>

              {resetError && <div className="text-sm text-red-400">{resetError}</div>}
              {resetSuccess && <div className="text-sm text-emerald-500 flex items-center gap-1.5"><CheckCircle2 size={15} />{resetSuccess}</div>}

              <Button
                type="submit"
                disabled={resetBusy}
                className="h-11 w-full bg-gradient-to-r from-[#d39c1e] to-[#f0c34d] text-black font-bold text-sm hover:from-[#e5ad29] hover:to-[#ffd45c] transition"
              >
                {resetBusy ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Resetting…
                  </>
                ) : (
                  "RESET PASSWORD"
                )}
              </Button>

              <button
                type="button"
                onClick={backToLogin}
                className={`flex items-center justify-center gap-1.5 w-full text-xs mt-1 hover:underline ${isDark ? "text-white/50" : "text-black/45"}`}
              >
                <ArrowLeft size={13} /> Back to Sign In
              </button>
            </form>
          )}

          <div className={`mt-7 flex items-start gap-2 text-[11px] ${isDark ? "text-white/45" : "text-black/40"}`}>
            <ShieldCheck size={14} className="text-[#c9971f] mt-0.5 shrink-0" />
            Secure business access for authorized GO OIL distribution partners.
          </div>
          <div className={`mt-3 text-center text-[10px] ${isDark ? "text-white/25" : "text-black/25"}`}>
            © {new Date().getFullYear()} GO OIL. One system. Complete control.
          </div>
        </div>
      </div>

    </div>
  );
}
